import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';
import Alert from '../components/Alert';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

function OptOut() {
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState('');

	const handleSubmit = async e => {
		e.preventDefault();
		if (!phone.trim() && !email.trim()) {
			return setError('Please provide your phone number or email address.');
		}
		setError('');
		setSubmitting(true);

		try {
			if (isSupabaseConfigured) {
				const { error: dbErr } = await supabase.from('sms_unsubscribes').insert([{
					phone: phone.trim() || null,
					email: email.trim().toLowerCase() || null,
					source: 'opt_out_form',
					created_at: new Date().toISOString(),
				}]);
				if (dbErr) throw dbErr;
			}
			setSuccess(true);
			setPhone('');
			setEmail('');
		} catch (err) {
			console.error(err);
			setError('We encountered an error. Please reply STOP to any message, or call (602) 390-2536.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>SMS Opt-Out | A-Z Home Improvement LLC</title>
				<meta name="description" content="Opt out of SMS text messages from A-Z Home Improvement LLC. Reply STOP to any message or use this form." />
			</Helmet>
			<PageHeader
				title="Opt Out of SMS Messages"
				subtitle="Use this form or reply STOP to any text message to unsubscribe from A-Z Home Improvement LLC SMS communications."
			/>

			<section className="section">
				<div className="container" style={{ maxWidth: 620 }}>
					<div className="card" style={{ padding: 28, marginBottom: 32, background: '#fff3cd', border: '1px solid #ffc107' }}>
						<h3 style={{ margin: '0 0 6px' }}>The Fastest Way to Opt Out</h3>
						<p style={{ margin: 0 }}>Simply reply <strong>STOP</strong> to any text message you receive from us. You'll get a confirmation and no further messages will be sent. No form needed.</p>
					</div>

					<h2>Or Use This Form</h2>
					<p className="helper">Provide your phone number or email address and we will remove you from our SMS communications list within one business day.</p>

					{success && (
						<Alert type="success">
							<strong>You've been opted out.</strong> You will no longer receive SMS messages from A-Z Home Improvement LLC. If you wish to re-subscribe in the future, you may do so by re-submitting consent through our contact form.
						</Alert>
					)}
					{error && <Alert type="error">{error}</Alert>}

					<form className="form" onSubmit={handleSubmit} noValidate>
						<div className="field">
							<label htmlFor="phone">Phone Number to Remove</label>
							<input
								id="phone"
								type="tel"
								value={phone}
								onChange={e => setPhone(e.target.value)}
								placeholder="(602) 555-1234"
								autoComplete="tel"
							/>
						</div>
						<div className="field">
							<label htmlFor="email">Email Address (optional, for confirmation)</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={e => setEmail(e.target.value)}
								placeholder="you@example.com"
								autoComplete="email"
							/>
						</div>
						<button type="submit" className="btn" disabled={submitting} style={{ fontSize: 15, padding: '11px 26px' }}>
							{submitting ? 'Processing…' : 'Opt Me Out'}
						</button>
					</form>

					<div style={{ marginTop: 36 }}>
						<h3>Other Ways to Opt Out</h3>
						<ul>
							<li><strong>Reply STOP</strong> — Text STOP in reply to any message</li>
							<li><strong>Call us:</strong> <a href="tel:+16023902536" style={{ color: 'var(--brand)' }}>(602) 390-2536</a></li>
							<li><strong>Email:</strong> <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a></li>
						</ul>
						<p className="helper">After opting out, you can re-subscribe at any time by submitting our contact form with SMS consent checked, or by texting START to our number.</p>
					</div>
				</div>
			</section>
		</>
	);
}

export default OptOut;
